import { useAppSelector } from '@/store/hooks';
import { useMemo } from 'react';
import FuseNavigationHelper from '@fuse/utils/FuseNavigationHelper';
import FuseUtils from '@fuse/utils/FuseUtils';
import type { FuseNavItemType } from '@fuse/core/FuseNavigation/types/FuseNavItemType';
import { selectNavigationAll } from '../store/navigationSlice';
import { useAuth } from '@/contexts/AuthContext';

function useNavigation() {
	const navigationData = useAppSelector(selectNavigationAll);
	const { user } = useAuth();

	const userRoles = useMemo(
		() => user?.roles?.map((r) => r.roleName) ?? [],
		[user?.roles],
	);

	const navigation = useMemo(() => {
		const _navigation = FuseNavigationHelper.unflattenNavigation(navigationData);

		function setAdditionalData(data: FuseNavItemType[]): FuseNavItemType[] {
			if (!data) return [];

			return data.map((item) => {
				const hasPermission = FuseUtils.hasPermission(item.auth, userRoles);

				return {
					...item,
					hasPermission,
					...(item?.children ? { children: setAdditionalData(item.children) } : {}),
				};
			});
		}

		return setAdditionalData(_navigation);
	}, [navigationData, userRoles]);

	const flattenNavigation = useMemo(() => {
		return FuseNavigationHelper.flattenNavigation(navigation);
	}, [navigation]);

	return { navigation, flattenNavigation };
}

export default useNavigation;
